import { Center, Loader, Text } from '@mantine/core';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ClientListItem } from 'src/admin/users/types';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { useGlobalConfirmation } from 'src/design-system/GlobalConfirmationProvider';
import { ReassignAdvisorConfirmModal } from '../components/ReassignAdvisorConfirmModal';
import { useClients } from '../hooks/useClients';
import { useFamily } from '../hooks/useFamily';
import { CreateFamilyPayload } from '../hooks/useCreateFamily';
import { useUpdateFamily } from '../hooks/useUpdateFamily';
import { getFamilyMemberClientIds } from '../utils';
import {
  clientsOfOtherAdvisers,
  roleModelPayloadFromInProgressFamilyV2,
  toggleMember,
  V2MemberRoles,
} from './dataHelpers';
import NameAndRolesStep from './NameAndRolesStep';
import SelectMembersStep from './SelectMembersStep';

export default function AddFamilyMemberPage() {
  const { familyId = '' } = useParams<{ familyId: string }>();
  const navigate = useNavigate();
  const { family, isLoading: isFamilyLoading } = useFamily(familyId);
  const { clients, isLoading: areClientsLoading } = useClients(
    { familyId },
    { enabled: !!familyId },
  );
  const { updateFamily, isLoading: isSaving } = useUpdateFamily();
  const { showConfirmation } = useGlobalConfirmation();
  const [step, setStep] = useState<'select' | 'details'>('select');
  const [newMembers, setNewMembers] = useState<ClientListItem[]>([]);
  const [pendingPayload, setPendingPayload] = useState<CreateFamilyPayload | null>(null);

  const profilePath = `/family/${familyId}/profile`;

  if (isFamilyLoading || areClientsLoading) {
    return (
      <Center h="100%">
        <Loader />
      </Center>
    );
  }

  if (!family) {
    return (
      <Center h="100%">
        <Text c="gray.6">This family could not be found.</Text>
      </Center>
    );
  }

  const clientById = Object.fromEntries(clients.map(client => [client.id, client]));
  // Use the full member set (main + secondary + role-model members); relying on
  // secondaryFamilyMembers alone drops members that only exist in `members`,
  // which would silently remove them from the family on the next update.
  const existingMembers = getFamilyMemberClientIds(family)
    .map(id => clientById[id])
    .filter((client): client is ClientListItem => client !== undefined);
  const allMembers = [...existingMembers, ...newMembers];

  const initialRoles: V2MemberRoles = {};
  if (family.mainFamilyMember.familyRoleId) {
    initialRoles[family.mainFamilyMember.clientId] = family.mainFamilyMember.familyRoleId;
  }
  family.members.forEach(member => {
    initialRoles[member.clientId] = member.familyRoleId ?? null;
  });

  const conflictingMembers = clientsOfOtherAdvisers(newMembers, family.advisorUserId);

  const performUpdate = async (payload: CreateFamilyPayload) => {
    try {
      await updateFamily(familyId, payload);
      showConfirmation(
        newMembers.length === 1
          ? `${newMembers[0].firstName} added to family`
          : `${newMembers.length} members added to family`,
      );
      navigate(`${profilePath}?member=${family.mainFamilyMember.clientId}`);
    } catch (error) {
      notifyAndLogError(
        'Failed to add family member',
        'An error occurred while adding the member. Please try again.',
        error,
      );
    }
  };

  const attemptSubmit = async (payload: CreateFamilyPayload) => {
    if (conflictingMembers.length > 0) {
      setPendingPayload(payload);
      return;
    }
    await performUpdate(payload);
  };

  const handleSubmitRoles = (familyName: string, roles: V2MemberRoles) =>
    attemptSubmit(
      roleModelPayloadFromInProgressFamilyV2(
        { selectedMembers: allMembers, roles, name: familyName },
        family.advisorUserId,
      ),
    );

  if (step === 'details') {
    return (
      <>
        <NameAndRolesStep
          selectedMembers={newMembers}
          onBack={() => setStep('select')}
          onCancel={() => navigate(profilePath)}
          onSubmit={handleSubmitRoles}
          isLoading={isSaving}
          initialFamilyName={family.name}
          initialRoles={initialRoles}
          actionLabel="Add to family"
          title="Set family roles"
        />
        <ReassignAdvisorConfirmModal
          opened={pendingPayload !== null}
          conflictingMembers={conflictingMembers}
          targetAdvisorUserId={family.advisorUserId}
          actionDescription={`Adding them to ${family.name}`}
          confirmLabel="Reassign and add to family"
          isLoading={isSaving}
          onConfirm={() => {
            if (pendingPayload) performUpdate(pendingPayload);
          }}
          onCancel={() => setPendingPayload(null)}
        />
      </>
    );
  }

  return (
    <SelectMembersStep
      title={`Select clients to include in ${family.name}`}
      selectedMembers={newMembers}
      onToggle={client => setNewMembers(previous => toggleMember(previous, client))}
      onCancel={() => navigate(profilePath)}
      onBack={() => navigate(profilePath)}
      onContinue={() => setStep('details')}
      continueDisabled={newMembers.length < 1}
    />
  );
}
